<template>
  <div>
    <FormHeader bookmarkable>
      Set your password

      <template #sub-heading>
        <template v-if="target">
          We sent a verification code to {{ target }}. Enter it and choose a
          password to finish signing in.
        </template>
        <template v-else>
          Enter the verification code we emailed you and choose a password to
          finish signing in.
        </template>
      </template>
    </FormHeader>

    <b-alert v-if="done" variant="primary" show class="text-center mb-4">
      <p class="mb-3">
        Your password is set. Continue to sign in with your new password.
      </p>
      <base-button type="primary" size="lg" block @click="continueToSignin">
        Continue to sign in
      </base-button>
    </b-alert>

    <validation-observer
      v-else
      v-slot="{ invalid }"
      ref="form"
      class="form form-sm mx-auto"
    >
      <b-form @submit.prevent="handleSubmit">
        <base-input
          v-model="formData.code"
          rules="required"
          name="Verification code"
          label="Verification code"
        ></base-input>

        <!-- Judged against the policy rules listed directly below it. maxlength
             is a hard stop at the real input, not just a checklist row - keeps
             this in lockstep with PasswordCriteriaChecklist's MAX_LENGTH. -->
        <BasePasswordInput
          v-model="formData.password"
          :status="passwordStatus"
          maxlength="64"
          valid-title="Meets the password requirements"
          invalid-title="Doesn't meet the password requirements yet"
          rules="required"
          name="New password"
          label="New password"
        />

        <!-- Sits under the field it describes, and appears only once typing
             starts. No :confirmation - matching is the confirm field's tick,
             not a row in here. -->
        <PasswordCriteriaChecklist
          :password="formData.password"
          @validity="(isValid) => (passwordAcceptable = isValid)"
        />

        <!-- Judged against the field above, not against the policy. Same
             maxlength as the password field - otherwise this one could accept
             more characters than the field it's meant to confirm. -->
        <BasePasswordInput
          v-model="formData.confirm"
          :status="confirmStatus"
          maxlength="64"
          valid-title="Passwords match"
          invalid-title="Passwords don't match"
          rules="required"
          name="Confirm password"
          label="Confirm password"
        />

        <base-button
          :disabled="
            invalid ||
            !passwordAcceptable ||
            !passwordsMatch ||
            processing ||
            starting
          "
          :loading="processing"
          type="primary"
          native-type="submit"
          size="lg"
          class="mt-4"
          block
        >
          {{ processing ? "Setting password..." : "Set password & sign in" }}
        </base-button>
      </b-form>

      <div class="mt-4 font-weight-bold text-center small">
        Didn't get a code?
        <a href="#" class="text-dark text-underline" @click.prevent="start"
          >Resend</a
        >
      </div>
    </validation-observer>
  </div>
</template>

<script>
import Vue from "vue";
import {
  computed,
  defineComponent,
  reactive,
  ref,
  onMounted,
} from "@vue/composition-api";
import { createVuexHelpers } from "vue2-helpers";
import { useRouter } from "vue2-helpers/vue-router";

import { AuthenticationService } from "@services";
import FormHeader from "@components/FormHeader";
import PasswordCriteriaChecklist from "@components/PasswordCriteriaChecklist";
import BasePasswordInput from "@components/base/base-password-input/BasePasswordInput";

export default defineComponent({
  name: "SsoSetPassword",
  components: {
    FormHeader,
    PasswordCriteriaChecklist,
    BasePasswordInput,
  },
  props: {
    email: {
      type: String,
      required: true,
    },
    // The redirect (OIDC) sign-in URL, used to fall back to the hosted flow if
    // native SSPR isn't available and to complete sign-in when auto sign-in
    // can't run (Entra password propagation lag).
    ssoLoginUrl: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const router = useRouter();
    const { useActions } = createVuexHelpers();
    const { setAccessToken, setRefreshToken, setUser } = useActions([
      "setAccessToken",
      "setRefreshToken",
      "setUser",
    ]);

    const formData = reactive({ code: "", password: "", confirm: "" });
    const flow = ref("");
    const target = ref("");
    const starting = ref(false);
    const processing = ref(false);
    const done = ref(false);
    // Driven by PasswordCriteriaChecklist - gates submit so a password Entra
    // would reject doesn't cost a round trip.
    const passwordAcceptable = ref(false);
    // The two fields answer different questions, so they get separate states.
    // Both stay null while empty - an untouched form shouldn't show crosses.

    // Field 1: does it satisfy Entra's policy? passwordAcceptable comes from
    // the checklist below, which lists exactly why when it doesn't.
    const passwordStatus = computed(() => {
      if (!formData.password) return null;
      return passwordAcceptable.value ? "valid" : "invalid";
    });

    // Field 2: does it match field 1? Nothing to do with the policy.
    const passwordsMatch = computed(
      () => !!formData.password && formData.password === formData.confirm
    );
    const confirmStatus = computed(() => {
      if (!formData.confirm) return null;
      return passwordsMatch.value ? "valid" : "invalid";
    });

    // Request a fresh code (also called on mount and on "Resend").
    const start = async () => {
      starting.value = true;
      const res = await AuthenticationService.ssoNativeSsprStart(
        props.email
      ).catch(() => null);
      starting.value = false;

      if (!res) {
        Vue.prototype.$toasted.show(
          "We couldn't send a code. Please try again.",
          { type: "error" }
        );
        return;
      }
      const data = (res.data && res.data.data) || {};
      // Native flow can't complete (e.g. social IdP) -> use the redirect flow.
      if (data.fallback === "redirect") {
        if (props.ssoLoginUrl) window.location.href = props.ssoLoginUrl;
        return;
      }
      flow.value = data.flow || "";
      target.value = data.target || "";
      Vue.prototype.$toasted.show("We sent you a verification code.");
    };

    const continueToSignin = () => {
      if (props.ssoLoginUrl) window.location.href = props.ssoLoginUrl;
    };

    const handleSubmit = async () => {
      // The submit button is disabled while this is false, but a Vue
      // @submit.prevent handler still fires on an Enter-key submit from an
      // input field regardless of the button's disabled attribute - so this
      // has to be re-checked here too, not just left to the button's state.
      if (!passwordAcceptable.value) {
        Vue.prototype.$toasted.show(
          "Please fix your password before continuing.",
          { type: "error" }
        );
        return;
      }
      if (formData.password !== formData.confirm) {
        Vue.prototype.$toasted.show("Passwords don't match.", { type: "error" });
        return;
      }
      processing.value = true;
      const res = await AuthenticationService.ssoNativeSsprVerify(
        flow.value,
        formData.code,
        formData.password
      ).catch((err) => {
        processing.value = false;
        const detail =
          (err && err.response && err.response.data && err.response.data.data) ||
          {};
        Vue.prototype.$toasted.show(
          detail.detail || "That didn't work. Please try again.",
          { type: "error" }
        );
        // Session token expired -> get a fresh code and start over.
        if (detail.code === "flow_expired") start();
        return null;
      });
      if (!res) return;
      processing.value = false;

      const data = (res.data && res.data.data) || {};
      if (data.access_token) {
        // Auto sign-in: store tokens + user exactly like the OTP login does.
        // The redirect-to-SSO flow lands here after a real browser navigation
        // (the OIDC callback), so vue-router-user-roles re-evaluates route
        // access on its own. This screen never navigates away on its own -
        // setting the store alone left the user stuck on this same form even
        // though sign-in had actually succeeded. Push explicitly rather than
        // rely on a reactive redirect elsewhere.
        Vue.prototype.$toasted.show("Password set. Signing you in...");
        setAccessToken(data.access_token);
        setRefreshToken(data.refresh_token);
        setUser(data.user);
        router.replace({ name: "Dashboard" }).catch(() => {});
        return;
      }
      // Password set, but auto sign-in isn't available yet (propagation lag) or
      // no session could be issued -> ask them to sign in with the new password.
      done.value = true;
    };

    onMounted(start);

    return {
      formData,
      target,
      starting,
      processing,
      done,
      passwordAcceptable,
      passwordStatus,
      confirmStatus,
      passwordsMatch,
      start,
      continueToSignin,
      handleSubmit,
    };
  },
});
</script>
